import StorySource from '../../data/story-source';
import LocalStorage from '../../data/local-storage';
import CONFIG from '../../config/app-config';

export default class NearbyModel {
  constructor() {
    this.nearbyStories = [];
    this.currentUserLocation = null;
    this.nearbyDistance = 500;
    this.isAuthenticated = false;
    this.user = {};
    this.bookmarks = [];
  }

  loadUserState() {
    const token = LocalStorage.getItem('heliaedu_token');
    const userData = LocalStorage.getItem('heliaedu_user');
    
    if (token && userData) {
      this.isAuthenticated = true;
      this.user = JSON.parse(userData);
      
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      const locationKey = `heliaedu_user_location_${this.user.id}`;
      const distanceKey = `heliaedu_user_distance_${this.user.id}`;
      
      this.bookmarks = LocalStorage.getObject(bookmarksKey) || [];
      this.currentUserLocation = LocalStorage.getObject(locationKey);
      this.nearbyDistance = parseInt(LocalStorage.getItem(distanceKey) || '500');
    } else {
      this.isAuthenticated = false;
      this.nearbyDistance = parseInt(LocalStorage.getItem('heliaedu_guest_distance') || '500');
    }
  }

  async getAllStoriesWithLocation() {
    const response = await StorySource.getAllStories({ 
      location: 1, // kalau 1 true kalau 0 false (bool sus)
      size: 100
    });
    if (!response.error) {
      return response.listStory || [];
    }
    throw new Error(response.message || 'Failed to fetch stories');
  }

  async getAllNearbyStories() {
    try {
      // Fetch dengan size lebih besar untuk nearby page
      const response = await StorySource.getAllStories({ 
        location: 1, // kalau 1 true kalau 0 false (bool sus)
        size: 200
      });
      
      if (!response.error && response.listStory) {
        return response.listStory.filter(story => story.lat && story.lon);
      }
      return [];
    } catch (error) {
      throw new Error('Failed to fetch nearby stories: ' + error.message);
    }
  }

  async getUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        this.fallbackToIPLocation(resolve, reject);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        () => {
          this.fallbackToIPLocation(resolve, reject);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  async fallbackToIPLocation(resolve, reject) {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        resolve({
          lat: data.latitude,
          lon: data.longitude
        });
      } else {
        reject(new Error('IP-based location failed'));
      }
    } catch (ipError) {
      reject(ipError);
    }
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  deg2rad(deg) {
    return deg * (Math.PI/180);
  }

  filterStoriesByDistance(stories) {
    if (!this.currentUserLocation) {
      return stories.filter(story => story.lat && story.lon);
    }
  
    return stories.filter(story => {
      if (!story.lat || !story.lon) return false;
      
      const distance = this.calculateDistance(
        this.currentUserLocation.lat,
        this.currentUserLocation.lon,
        story.lat,
        story.lon
      );
      return distance <= this.nearbyDistance;
    });
  }

  async getLocationName(lat, lon) {
    try {
      const response = await fetch(
        `https://api.maptiler.com/geocoding/${lon},${lat}.json?key=${CONFIG.MAP_TILER_KEY}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const placeName = data.features[0].place_name;
        const parts = placeName.split(',');
        
        if (parts.length >= 2) {
          return `${parts[0].trim()}, ${parts[1].trim()}`;
        }
        return parts[0].trim();
      }
    } catch (error) {

    }

    return `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
  }

  getMapStyleConfig(isDark = false, layerType = 'streets') {
    let styleUrl;
    switch(layerType) {
      case 'satellite':
        styleUrl = `https://api.maptiler.com/maps/satellite/style.json?key=${CONFIG.MAP_TILER_KEY}`;
        break;
      case 'terrain':
        styleUrl = `https://api.maptiler.com/maps/outdoor-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
        break;
      default:
        styleUrl = isDark 
          ? `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${CONFIG.MAP_TILER_KEY}`
          : `https://api.maptiler.com/maps/streets-v2/style.json?key=${CONFIG.MAP_TILER_KEY}`;
    }
    return styleUrl;
  }

  getMapConfig(isDark = false) {
    return {
      container: 'nearby-map',
      style: this.getMapStyleConfig(isDark),
      center: [113.9213, -0.7893],
      zoom: 5
    };
  }

  getRangeCircleData(radiusInKm) {
    if (!this.currentUserLocation) return null;

    const center = [this.currentUserLocation.lon, this.currentUserLocation.lat];
    const points = 64;
    const coords = [];

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dx = radiusInKm * Math.cos(angle) / (111.32 * Math.cos(this.deg2rad(center[1])));
      const dy = radiusInKm * Math.sin(angle) / 110.574;
      coords.push([center[0] + dx, center[1] + dy]);
    }
    coords.push(coords[0]);

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords]
      }
    };
  }

  setNearbyStories(stories) {
    this.nearbyStories = stories;
  }

  setCurrentUserLocation(location) {
    this.currentUserLocation = location;
    
    if (this.isAuthenticated && this.user.id) {
      const locationKey = `heliaedu_user_location_${this.user.id}`;
      LocalStorage.setObject(locationKey, location);
    }
  }

  updateDistance(newDistance) {
    this.nearbyDistance = newDistance;
    
    if (this.isAuthenticated && this.user.id) {
      const distanceKey = `heliaedu_user_distance_${this.user.id}`;
      LocalStorage.setItem(distanceKey, this.nearbyDistance.toString());
    } else {
      LocalStorage.setItem('heliaedu_guest_distance', this.nearbyDistance.toString());
    }
  }

  async loadNearbyUsersData(currentUserLocation, nearbyDistance) {
    const response = await StorySource.getAllStories({ location: 1, size: 100 });
  
    if (!response.error && response.listStory) {
      const storiesWithLocation = response.listStory.filter(story => story.lat && story.lon);
      
      let nearbyStoriesForUsers = storiesWithLocation;
      if (currentUserLocation) {
        nearbyStoriesForUsers = storiesWithLocation.filter(story => {
          const distance = this.calculateDistance(
            currentUserLocation.lat,
            currentUserLocation.lon,
            story.lat,
            story.lon
          );
          return distance <= nearbyDistance;
        });
      }
      
      return nearbyStoriesForUsers.slice(0, 3);
    }
    
    throw new Error('Failed to load nearby users');
  }

  isBookmarked(storyId) {
    return this.bookmarks.some(b => b.storyId === storyId);
  }

  addBookmark(storyId) {
    const story = this.nearbyStories.find(s => s.id === storyId);
    
    if (story && !this.isBookmarked(storyId)) {
      const bookmark = {
        storyId: story.id,
        userName: story.name,
        userPhoto: `${CONFIG.AVATAR_BASE_URL}?seed=${encodeURIComponent(story.name)}`,
        description: story.description,
        photoUrl: story.photoUrl,
        lat: story.lat,
        lon: story.lon,
        savedAt: new Date().toISOString()
      };
      
      this.bookmarks.push(bookmark);
      
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      LocalStorage.setObject(bookmarksKey, this.bookmarks);
      
      return bookmark;
    }
    return null;
  }

  removeBookmark(storyId) {
    const oldLength = this.bookmarks.length;
    this.bookmarks = this.bookmarks.filter(b => b.storyId !== storyId);
    
    if (this.bookmarks.length < oldLength) {
      const bookmarksKey = `heliaedu_user_bookmarks_${this.user.id}`;
      LocalStorage.setObject(bookmarksKey, this.bookmarks);
      return true;
    }
    return false;
  }
}